import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useUnscrollableBody, useWindowDimensions } from '../../common/utils/windowDimensions';
import UserContext from '../../common/utils/getContext';
import StructureTableButtons from './components/StructureTableButtons/StructureTableButtons';
import StructureBreadCrumbsUser from './components/StructureBreadCrumbsUser/StructureBreadCrumbsUser';
import { changeGridData, afterOpenFunction, getUserReferals, transformData, thead, getCounterTree } from './initData';
import { LangContext } from '../../common/decorators/Language';
import css from './structure.module.scss';
import GridComponent from '../../widgets/grid/GridComponent/GridComponent';
import StructureSearchModal from './components/StructureSearchModal/StructureSearchModal';
import classNames from 'classnames';
import { Portal, svgSprite } from '../../hybrid/wrappers';
import Counter from './components/Counter/Counter';
import { dataSender } from '../../common/utils/router';
import { API_SERVER_URL } from '../../common/utils/utils';

const StructureView = ({ userData, variant, dataStats }) => {
    const { translation } = useContext(LangContext) || {};
    const { userToken, setAlertData, lang, display_country } = useContext(UserContext);
    const [showModal, setShowModal] = useState(false);
    const [structureViewData, setStructureViewData] = useState(userData);
    const [breadCrumbsUsers, setBreadCrumbsUsers] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [tableVariant, setTableVariant] = useState(variant);
    const [loadingStructure, setLoadingStructure] = useState(false);
    const [forceOpen, setForceOpen] = useState(false);
    const [showReloadBt, setShowReloadBt] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [openUserId, setOpenUserId] = useState('');
    const sortFunc = (attr) => {
        setSortBy((s) => {
            if (s.includes(attr)) {
                if (s.includes('-')) {
                    return attr;
                } else {
                    return `-${attr}`;
                }
            } else {
                return attr;
            }
        });
    };
    useUnscrollableBody(fullScreen, 'FullScreenStructure');
    const onEsc = (e) => {
        if (e.keyCode === 27) {
            setFullScreen(false);
        }
    };
    useEffect(() => {
        if (document) {
            document.addEventListener('keydown', onEsc);
        }
        return () => {
            document.removeEventListener('keydown', onEsc);
        };
    }, []);

    const afterOpenSwitherClick = useCallback(
        (data, type = false) => {
            setLoadingStructure(false);
            if (tableVariant == 1) {
                setOpenUserId(userData?.data?.[0]);
            } else {
                setOpenUserId(data);
            }
            setSortBy('');
            afterOpenFunction(data, tableVariant, setBreadCrumbsUsers, type);
            return true;
        },
        [tableVariant, userData]
    );

    useEffect(() => {
        if (!!Number(display_country)) {
            setTableVariant(2);
        }
    }, [display_country]);

    const changeGridContent = useCallback(
        (user) =>
            changeGridData(
                user,
                userData,
                setStructureViewData,
                breadCrumbsUsers,
                setBreadCrumbsUsers,
                userToken,
                setAlertData,
                lang,
                display_country
            ),
        [userData, breadCrumbsUsers, userToken, setAlertData, lang, display_country]
    );

    const widthDimensions = useWindowDimensions();
    const [screenWidth, setMobile] = useState(false);
    useEffect(() => {
        widthDimensions < 768 ? setMobile(true) : setMobile(false);
    }, [widthDimensions]);

    // const [showAlert, setShowAlert] = useState(true);

    const exportData = useMemo(
        () => ({
            filename: 'structure',
            fullUrl:
                (structureViewData?.data && structureViewData?.data[0]?.links?.descendants_export) ||
                (structureViewData?.data && structureViewData?.data[0]?.links?.descendants + '/export')
        }),
        [structureViewData]
    );

    useEffect(() => {
        if (sortBy) {
            setTableVariant(2);
            dataSender(
                `${API_SERVER_URL}/api/v1/user/partners/${openUserId.id}/descendants?filter[parent_id]=${openUserId.id}${
                    sortBy ? `&sort=${sortBy},model_depth,position` : ''
                },&page[size]=20&include=marketingProfile,sponsor,profile.country,profile.city`,
                'GET',
                false,
                (response) => {
                    setAlertData(false);
                    setStructureViewData(transformData(response.data));
                    const indexOfUser = breadCrumbsUsers.reduce((res, item, idx) => {
                        if (item.username === openUserId.username) res = idx;
                        return res;
                    }, 0);
                    const newBreadCrumbsUsers = breadCrumbsUsers.slice(0, indexOfUser + 1);
                    setBreadCrumbsUsers(newBreadCrumbsUsers.length ? newBreadCrumbsUsers : false);
                },
                (data) => {
                    if (data.message) {
                        setAlertData({ type: 'error', cont: data.message });
                    } else {
                        setAlertData({ type: 'error', cont: data?.false });
                    }
                },
                userToken,
                lang,
                false,
                false,
                false,
                !!Number(display_country) ? { 'Display-Country': '1' } : null
            );
        }
    }, [sortBy]);

    const gridStyles = useMemo(() => {
        return {
            activeRow: {
                deleted: { background: 'var(--notSuccessOp5)' },
                blocked: { background: 'rgba(var(--orange) / 0.1)' },
                is_new_partner: { background: 'var(--successSuccess01Op5)' }
            },
            username: {
                image: css.userImage,
                wrapper: css.usernameWrapper,
                td: {
                    className: css.structureTdFirst
                },
                th: {
                    className: css.structureThFirst
                }
            },
            sponsor: {
                image: css.userImage
            },
            contacts: {
                td: {
                    className: css.contactsTd,
                    div: {
                        className: css.contactsDiv
                    }
                },
                th: {
                    className: css.contactsTh
                },
                openSwitcher: {
                    className: css.contactsOpenSwitcher
                }
            }
        };
    }, []);

    const dataCounter = useMemo(() => {
        return getCounterTree(dataStats?.data, screenWidth, translation);
    }, [dataStats?.data, screenWidth, translation]);

    const loadData = useCallback(() => {
        setStructureViewData(userData);
        setForceOpen(userData);
        setShowReloadBt(false);
        changeGridContent(userData);
    }, [userData]);

    return (
        <>
            {!!fullScreen && (
                <Portal customClass='modalTop' fullWidth fullHeight>
                    <div className={css.tableWrapper} style={{ width: '100%', minHeight: '100%' }}>
                        <StructureTableButtons
                            screenWidth={screenWidth}
                            setShowModal={setShowModal}
                            showModal={showModal}
                            fullScreen={fullScreen}
                            setfullScreen={setFullScreen}
                            setTableVariant={setTableVariant}
                            variant={tableVariant}
                            initData={userData}
                            setStructureViewData={setStructureViewData}
                            setBreadCrumbsUsers={setBreadCrumbsUsers}
                            exportData={exportData}
                            loadData={loadData}
                            showReloadBt={showReloadBt}
                            display_country={display_country}
                            translation={translation}
                        />

                        {showModal && (
                            <StructureSearchModal
                                setShowModal={setShowModal}
                                setForceOpen={setForceOpen}
                                setStructureViewData={setStructureViewData}
                                afterOpenSwitherClick={afterOpenSwitherClick}
                            />
                        )}
                        <StructureBreadCrumbsUser breadCrumbsUsers={breadCrumbsUsers} changeGridContent={changeGridContent} />
                        <div className={css.gridWrapper}>
                            <GridComponent
                                gridStyles={gridStyles}
                                handlers={{}}
                                viewThead={thead}
                                viewData={structureViewData}
                                setGridData={setStructureViewData}
                                gridName='structure'
                                variant={tableVariant}
                                convertData={transformData}
                                openSwitcher={{
                                    handlers: { onClick: getUserReferals },
                                    afterOpenSwitherClick,
                                    loadingStructure,
                                    setLoadingStructure
                                }}
                                sticky={{
                                    rightStickyColumnName: 'contacts'
                                }}
                            />
                        </div>
                    </div>
                </Portal>
            )}
            <div onClick={() => setShowModal(true)} className={classNames('circleButton', css.searchButtonMobile)}>
                {svgSprite('search-gt')}
            </div>
            <Counter data={dataCounter} />
            {/* <BlockWithShine>
                <div className={css.distributors}>
                    <p className={css.distributorsValue}>{dataStats?.data?.base_stats?.descendants_count || '0'}</p>
                    <p className={css.distributorsLabel}>
                        <T
                            textName='structure-distributors-count'
                            defaultStr='Всего дистрибьюторов в структуре'
                            page='/structure'
                        />
                    </p>
                </div>
            </BlockWithShine> */}
            <div className={css.tableWrapper} style={{ position: 'relative' }}>
                <StructureTableButtons
                    screenWidth={screenWidth}
                    setShowModal={setShowModal}
                    showModal={showModal}
                    fullScreen={fullScreen}
                    setfullScreen={setFullScreen}
                    setTableVariant={setTableVariant}
                    variant={tableVariant}
                    initData={userData}
                    setStructureViewData={setStructureViewData}
                    setBreadCrumbsUsers={setBreadCrumbsUsers}
                    exportData={exportData}
                    loadData={loadData}
                    showReloadBt={showReloadBt}
                    translation={translation}
                    display_country={display_country}
                />
                {showModal && (
                    <StructureSearchModal
                        setShowModal={setShowModal}
                        setStructureViewData={setStructureViewData}
                        afterOpenSwitherClick={afterOpenSwitherClick}
                        setShowReloadBt={setShowReloadBt}
                        setForceOpen={setForceOpen}
                    />
                )}
                <StructureBreadCrumbsUser breadCrumbsUsers={breadCrumbsUsers} changeGridContent={changeGridContent} />
                <div className={css.gridWrapper}>
                    <GridComponent
                        gridStyles={gridStyles}
                        handlers={{}}
                        viewThead={thead}
                        viewData={structureViewData}
                        setGridData={setStructureViewData}
                        gridName='structure'
                        variant={tableVariant}
                        convertData={transformData}
                        pageSettings={{
                            sort: {
                                activity: {
                                    func: () => {
                                        sortFunc('activity');
                                    }
                                },
                                rank: {
                                    func: () => {
                                        sortFunc('rank');
                                    }
                                },
                                package: {
                                    func: () => {
                                        sortFunc('package');
                                    }
                                },
                                branch: {
                                    func: () => {
                                        sortFunc('branch_position');
                                    }
                                },
                                username: {
                                    func: () => {
                                        sortFunc('user_name');
                                    }
                                },
                                sponsor: {
                                    func: () => {
                                        sortFunc('sponsor_name');
                                    }
                                },
                                register_date: {
                                    func: () => {
                                        sortFunc('created_at');
                                    }
                                },
                                country: {
                                    func: () => {
                                        sortFunc('country');
                                    }
                                }
                            }
                        }}
                        openSwitcher={{
                            handlers: { onClick: getUserReferals },
                            afterOpenSwitherClick,
                            loadingStructure,
                            setLoadingStructure,
                            defaultOpened: userData?.data?.constructor === Array ? [...userData?.data] : [],
                            forceOpen,
                            setForceOpen
                        }}
                        sticky={{
                            rightStickyColumnName: 'contacts'
                        }}
                    />
                </div>
                {/* <OfficeGridFilter
                    grid={{
                        initData: gridData,
                        setInitData: setGridData,
                        openSwitcher: {
                            handlers: { onClick: getUserReferals },
                            afterOpenSwitherClick,
                            loadingStructure,
                            setLoadingStructure
                        },
                        variant: tableVariant,
                        gridStyles,
                        gridName: 'structure'
                    }}
                    convertData={transformData}
                    pageSettings={{
                        structure: 'counter-head',
                        counterElement: (
                            <div className={css.distributors}>
                                <p className={css.distributorsValue}>{dataStats?.data?.base_stats?.descendants_count || '0'}</p>
                                <p className={css.distributorsLabel}>
                                    <T
                                        textName='structure-distributors-count'
                                        defaultStr='Всего дистрибьюторов в структуре'
                                        page='/structure'
                                    />
                                </p>
                            </div>
                        ),
                        gridSmallHeader: (
                            <div>
                                <StructureTableButtons
                                    screenWidth={screenWidth}
                                    setShowModal={setShowModal}
                                    showModal={showModal}
                                    fullScreen={fullScreen}
                                    setfullScreen={setFullScreen}
                                    setTableVariant={setTableVariant}
                                    variant={tableVariant}
                                    initData={userData}
                                    setStructureViewData={setStructureViewData}
                                    setBreadCrumbsUsers={setBreadCrumbsUsers}
                                    exportData={exportData}
                                    loadData={loadData}
                                    showReloadBt={showReloadBt}
                                    translation={translation}
                                />
                                <StructureBreadCrumbsUser
                                    breadCrumbsUsers={breadCrumbsUsers}
                                    changeGridContent={changeGridContent}
                                />
                            </div>
                        )
                    }}
                    textsData={{
                        globalSearthTitle: (
                            <T textName='structure-search-title' defaultStr='Поиск дистрибьютора' page='/structure' />
                        ),
                    }}
                    filter={{
                        data: filterFields,
                        url: `${API_SERVER_URL}/api/v1/user/partners/${profileData.id}/descendants`,
                        filterParams: 'include=marketingProfile',
                        funcs: (resetFilter) => ({
                            cancelFuncs: {
                                web: {
                                    onClick: () => {
                                        resetFilter();
                                    }
                                }
                            }
                        }),
                        globalAttr: 'username'
                    }}
                    sticky={{
                        rightStickyColumnName: 'contacts'
                    }}
                /> */}
            </div>
        </>
    );
};
export default StructureView;
